import { Link } from "react-router-dom";
import { Trash2, Mail, ArrowLeft, Smartphone, Globe } from "lucide-react";

/* ============================================================================
 * Account Deletion page — required by Google Play "User account deletion"
 * policy (2024). Submitted to Play Console under Data Safety → Account
 * deletion URL. Must be publicly accessible without login.
 *
 * This page does not execute deletion itself; it explains the available
 * channels (in-app, mailto, email) and the deletion / retention scope.
 * Kept in sync with /privacy Section 5.
 * ========================================================================== */

const SUPPORT_EMAIL = "privacy@ealoom.com";
const COMPANY_LEGAL_NAME = "Lambda Flow LLC";
const COMPANY_BRAND = "Ealoom";

const MAILTO_SUBJECT = "Account deletion request";
const MAILTO_BODY = `Hi Ealoom Privacy team,

I am requesting permanent deletion of my account and associated personal data, as described in the Ealoom Privacy Policy.

Account email: <please confirm this matches the email you are sending from>
Role: <Driver / Dispatcher / Other>
Trucking company name (if known): <optional>

Thank you.`;

const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
  MAILTO_SUBJECT
)}&body=${encodeURIComponent(MAILTO_BODY)}`;

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-10">
      <h2 className="text-xl font-semibold text-slate-900 mb-3">{title}</h2>
      <div className="space-y-3 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function AccountDeletion() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
        </Link>

        <header className="mt-6 flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
            <Trash2 className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Delete your account
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              {COMPANY_LEGAL_NAME} (d/b/a {COMPANY_BRAND})
            </p>
          </div>
        </header>

        <p className="mt-8 text-slate-700 leading-relaxed">
          You can permanently delete your {COMPANY_BRAND} account and the
          personal data associated with it. This page explains the channels
          available to you, what we delete, what we are legally required to
          keep, and how long the process takes. It applies to both Driver
          mobile-app accounts and Dispatcher / web-console accounts.
        </p>
        <p className="mt-3 text-slate-700 leading-relaxed">
          If you are a Driver employed by a trucking company that uses{" "}
          {COMPANY_BRAND}, deleting your account here removes <em>your</em>{" "}
          personal data &mdash; it does not affect the records the trucking
          company is required to keep for its own books.
        </p>

        <Section id="how-to-delete" title="1. How to request deletion">
          <div className="grid gap-3 sm:grid-cols-1">
            <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-white">
              <Smartphone className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-slate-900 mb-1">
                  From the Driver mobile app (recommended)
                </p>
                <p className="text-sm">
                  Open the app, go to{" "}
                  <span className="font-mono">Settings → Delete Account</span>
                  , and confirm. The action does not require approval from your
                  dispatcher and is processed within minutes.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-white">
              <Mail className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-slate-900 mb-1">
                  By email (no app required)
                </p>
                <p className="text-sm">
                  Send an email to{" "}
                  <a
                    className="text-blue-600 underline"
                    href={`mailto:${SUPPORT_EMAIL}`}
                  >
                    {SUPPORT_EMAIL}
                  </a>{" "}
                  <strong>from the email address tied to your account</strong>.
                  We will verify the request and complete deletion within 45
                  days. Use the button below to open a pre-filled message:
                </p>
                <a
                  href={mailtoHref}
                  className="inline-flex items-center mt-3 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
                >
                  <Mail className="w-4 h-4 mr-2" /> Open pre-filled email
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-white">
              <Globe className="w-5 h-5 text-slate-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-slate-900 mb-1">
                  From the web console (Dispatchers)
                </p>
                <p className="text-sm">
                  If you are a Dispatcher, Accountant, Document Clerk, or
                  Manager, email{" "}
                  <a
                    className="text-blue-600 underline"
                    href={`mailto:${SUPPORT_EMAIL}`}
                  >
                    {SUPPORT_EMAIL}
                  </a>{" "}
                  from your registered address. The trucking-company owner may
                  also remove other users directly from the web console under{" "}
                  <span className="font-mono">Settings → Team</span>.
                </p>
              </div>
            </div>
          </div>
        </Section>

        <Section id="what-we-delete" title="2. What we delete">
          <p>
            Within 45 days of a confirmed request we delete or irreversibly
            anonymize the following data tied to your account:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Name, email address, phone number, mailing address</li>
            <li>Profile photo</li>
            <li>Federated sign-in subjects (Google / Apple)</li>
            <li>
              Driver documents: CDL, medical card, TWIC, drug-test record,
              insurance certificate, EIN document
            </li>
            <li>Push notification token (FCM)</li>
            <li>Chat messages authored by you</li>
            <li>GPS location history beyond what is required by Section 3</li>
          </ul>
          <p>
            All active sessions on every device are invalidated immediately
            upon request. The email address becomes available again for
            re-registration.
          </p>
        </Section>

        <Section id="what-we-retain" title="3. What we retain (and why)">
          <p>
            U.S. trucking regulations and tax law require us &mdash; and the
            trucking company you work with &mdash; to keep certain operational
            records even after an individual leaves. After deletion we retain
            only the following in <strong>anonymized</strong> form:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Task identifiers (no driver name, no GPS path tied to identity)
              &mdash; 3 years after termination per{" "}
              <span className="font-mono">49 CFR §391.51</span>
            </li>
            <li>
              Invoice / payment records &mdash; up to 7 years per IRS
              recordkeeping rules
            </li>
            <li>
              Audit-log entries referencing the deleted user ID &mdash; 12
              months
            </li>
          </ul>
          <p>
            After anonymization these records contain no personally identifying
            information and cannot be linked back to you.
          </p>
        </Section>

        <Section id="timeline" title="4. Timeline">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>In-app deletion:</strong> processed within minutes;
              session invalidated immediately.
            </li>
            <li>
              <strong>Email request:</strong> verified and completed within{" "}
              <strong>45 days</strong>.
            </li>
            <li>
              <strong>Backups:</strong> anonymized records persist in our
              backup tier for up to an additional 90 days before they expire on
              the normal rotation cycle.
            </li>
          </ul>
        </Section>

        <Section id="contact" title="5. Questions">
          <p>
            For questions about this process, your privacy rights, or to check
            on a pending request, email{" "}
            <a
              className="text-blue-600 underline"
              href={`mailto:${SUPPORT_EMAIL}`}
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
          <p>
            For the full description of data we collect, how we use it, and
            additional rights you may have, see our{" "}
            <Link to="/privacy" className="text-blue-600 underline">
              Privacy Policy
            </Link>
            .
          </p>
        </Section>

        <footer className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <Link to="/" className="text-blue-600 hover:underline">
            ← Back to home
          </Link>
        </footer>
      </div>
    </div>
  );
}

export default AccountDeletion;
